import React, { useState, useEffect } from 'react';
import { navigate } from 'gatsby';
import BodyClassName from 'react-body-classname';
import DefaultLayoutComponent from '../modules/layouts/DefaultLayoutComponent';

const PoliciesPage = ({ location }) => {
  const [activeTab, setActiveTab] = useState('privacy-policy');

  const tabs = [
    { id: 'privacy-policy', label: 'Privacy Policy' },
    { id: 'cookie-policy', label: 'Cookie Policy' },
    { id: 'gdpr-policy', label: 'GDPR Policy' },
    { id: 'whistleblower-policy', label: 'Whistleblower Policy' },
    { id: 'financial-transparency', label: 'Financial Transparency' },
    { id: 'financial-standards', label: 'Financial Standards' },
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && tabs.find(t => t.id === tabParam)) {
      setActiveTab(tabParam);
    } else if (!tabParam) {
      navigate('?tab=privacy-policy', { replace: true });
    }
  }, [location.search]);

  const renderContent = () => {
    switch (activeTab) {
      case 'privacy-policy':
        return (
          <div className="policy-item">
            <h2 className="desktop-only-title">Policies & Practices</h2>
            <div className="policy-section">
              <h3>Privacy Summary</h3>
              <p>Lineage Journey is committed to protecting your privacy. Some of our services—such as processing donations, orders, or applications—require the collection of personal information. In these cases, we collect only what is necessary and take great care to safeguard it. This statement outlines the types of information collected through our website(s), how it is used, and the measures we take to protect it.</p>
            </div>
            <div className="policy-section">
              <h3>Information We Collect</h3>
              <p>Lineage Journey collects personally identifiable information that you voluntarily provide, such as your name, email address, and mailing address. We may also collect non-identifying demographic information, including ZIP code, age, and gender. Some of this anonymous data is gathered through Analytics Demographics and Interest Reporting, from which you may opt out if you choose.</p>
              <p>In addition, certain technical information about your computer or device—such as IP address, browser type, and browsing device—is collected automatically to help us maintain and improve our services.</p>
            </div>
            <div className="policy-section">
              <h3>How We Use Your Information</h3>
              <p>Personal information is used to provide the services you request, communicate with you about products or services offered by Lineage Journey, and support the operation of our website. For example, we collect information through web forms when you request materials or make a purchase.</p>
              <p>Lineage Journey does not sell, rent, or lease your personal information to third parties. We may share data with trusted service providers who assist with tasks such as statistical analysis, communications, customer support, or order fulfillment. These partners are authorized to use your information only for these purposes and are required to keep it confidential.</p>
              <p>We may disclose personal information only when required by law or legal process, or when necessary to protect the rights, property, or safety of Lineage Journey.</p>
            </div>
            <div className="policy-section">
              <h3>Data Security</h3>
              <p>All personally identifiable information is stored on secure servers in controlled environments and protected against unauthorized access, use, or disclosure. Lineage Journey uses industry-standard security and encryption practices to safeguard sensitive information during collection and transmission.</p>
            </div>
            <div className="policy-section">
              <h3>Changes to This Policy</h3>
              <p>This privacy statement may be updated from time to time. We encourage you to review it periodically to stay informed about how we protect your information.</p>
            </div>
            <div className="policy-section">
              <h3>Contact Information</h3>
              <p>If you have any questions or concerns regarding this privacy statement or our data practices, please contact us at:</p>
              <p>
                <strong>Email:</strong><br />
                info@lineagejourney.com
              </p>
              <p>
                <strong>Mail:</strong><br />
                Lineage Journey<br />
                PO Box 405<br />
                Grass Valley, CA 95945<br />
                United States
              </p>
            </div>
          </div>
        );
      case 'cookie-policy':
        return (
          <div className="policy-item">
            <h2 className="desktop-only-title">Policies & Practices</h2>
            <div className="policy-section">
              <h3>Cookie Policy</h3>
              <p>A cookie is a small text file stored on your computer by a web server. Cookies cannot run programs or transmit viruses. Each cookie is uniquely assigned to your browser and can be read only by the web server that created it.</p>
              <p>LineageJourney.com uses cookies to improve your browsing experience. For instance, if you leave the site after placing items in your shopping cart, cookies allow LineageJourney.com to remember those items when you return.</p>
              <p>You may control or disable cookies through your browser settings or by using the cookie preference manager available in the footer of our website. Please be aware that disabling cookies may affect the functionality and overall experience of LineageJourney.com and other websites you visit.</p>
            </div>
          </div>
        );
      case 'gdpr-policy':
        return (
          <div className="policy-item">
            <h2 className="desktop-only-title">Policies & Practices</h2>
            <div className="policy-section">
              <h3>GDPR Policy</h3>
              <p>The General Data Protection Regulation (GDPR) came into effect on May 25, 2018, establishing requirements for organizations that collect, control, or process the personal data of residents of the European Union.</p>
              <p>Lineage Journey is committed to protecting your privacy and handling personal data responsibly. We acknowledge our obligation to collect, use, and safeguard personal information in compliance with applicable data protection laws and recognized best practices. If you have any questions regarding our GDPR compliance, please contact us at:</p>
              <p><strong>info@lineagejourney.com</strong></p>
            </div>
          </div>
        );
      case 'whistleblower-policy':
        return (
          <div className="policy-item">
            <h2 className="desktop-only-title">Policies & Practices</h2>
            <div className="policy-section">
              <h3>Lineage Journey Whistle blowing Policy</h3>
              <p>This applies to all Directors, Officers, Employees and volunteers.</p>
              <p>We aim to maintain high standards of integrity in everything we do. However all organizations can occasionally be affected by conduct that is dangerous against the law, or that breaches ethical or professional codes.</p>
              <p>If you have any such concerns, we encourage you to report them immediately - this is called whistleblowing. We will take your concerns seriously, they will be thoroughly investigated, and you can be confident there will be no reprisals.</p>
              <p>You can raise the following concerns:</p>
              <ul>
                <li>Any activity you suspect is criminal.</li>
                <li>Any activity you suspect may damage the environment.</li>
                <li>Any activity you suspect puts health at risk.</li>
                <li>Any activity you suspect is bribery.</li>
                <li>Any failure to comply with legal or regulatory obligations.</li>
                <li>Any failure to meet professional standards.</li>
                <li>Any attempt to conceal one or more of these activities.</li>
              </ul>
            </div>
            <div className="policy-section">
              <h3>How to raise a concern</h3>
              <p>In most cases you should raise your concern with your line manager either in writing or face to face. If you would prefer not to do this then you can raise your concern with a director in writing stating that you are writing under this policy. You will be invited to a meeting to discuss your concerns. We will investigate your concerns and keep you updated about how the investigation is progressing, although we may not be able to provide details.</p>
            </div>
            <div className="policy-section">
              <h3>Confidentiality and anonymity</h3>
              <p>There is a significant difference between wanting to keep your concerns confidential and making a disclosure anonymously. Anonymous whistle blowing is very difficult and sometimes impossible to investigate therefore we actively discourage anonymous whistle blowing. We commit to protecting your confidentiality and will be happy to discuss this with you to ensure we can do everything you expect in this regard.</p>
            </div>
            <div className="policy-section">
              <h3>Protection</h3>
              <p>Concerns raised under this policy will be protected and respected whatever the outcome of the investigation and there will be no reprisals. Mistreatment will not be tolerated.</p>
            </div>
            <div className="policy-section">
              <h3>Taking your concerns outside</h3>
              <p>In some exceptional circumstances, you may need to go to an external body such as a regulator. In such cases you should contact the relevant regulatory body for guidance.</p>
            </div>
          </div>
        );
      case 'financial-transparency':
        return (
          <div className="policy-item">
            <h2 className="desktop-only-title">Policies & Practices</h2>
            <div className="policy-section">
              <h3>Financial Transparency</h3>
              <p>Lineage Journey is committed to financial transparency, ensuring access to information about our fiscal and financial activities. Full copies of the audited financial statements and the 990 form are provided to our board of directors and can be made available upon request.</p>
            </div>
          </div>
        );
      case 'financial-standards':
        return (
          <div className="policy-item">
            <h2 className="desktop-only-title">Policies & Practices</h2>
            <div className="policy-section">
              <h3>Financial Standards</h3>
              <p>We assure you that every contribution you make to Lineage Journey will be utilized to its fullest potential, adhering to the highest standards of financial integrity. As a 501(c)(3) non-profit organization, all donations are tax-deductible. We are dedicated to exemplary Christian stewardship, undergoing annual audits by an external accounting firm and being accountable to a board of directors that convenes biannually for financial reviews. The financial support you provide is managed with prayer, integrity, and strategic planning to maximize its impact on those in need.</p>
              <p>We sincerely appreciate your prayers and support.</p>
              <p>Our ministry relies on your generosity, and we thank you for being an essential part of our mission!</p>
            </div>
          </div>
        );
      default:
        const selectedTab = tabs.find(t => t.id === activeTab);
        return (
          <div className="policy-item">
            <h2 className="desktop-only-title">Policies & Practices</h2>
            <div className="policy-section">
              <h3>{selectedTab ? selectedTab.label : 'Policy'}</h3>
              <p>Content for {selectedTab ? selectedTab.label : 'this policy'} is coming soon.</p>
            </div>
          </div>
        );
    }
  };

  return (
    <BodyClassName className="body-light page-policies no-nav-transparency">
      <DefaultLayoutComponent title="Policies & Practices">
        <main className="site-main policies-page">
          <div className="wrapper wrapper-xl pd-x-md">
            <div className="policies-container">
              <aside className="policies-sidebar">
                <h1 className="mobile-only-title">Policies & Practices</h1>
                <nav className="policy-tabs">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      className={`policy-tab ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => navigate(`?tab=${tab.id}`)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </aside>
              <section className="policies-content">
                {renderContent()}
              </section>
            </div>
          </div>
        </main>
      </DefaultLayoutComponent>
    </BodyClassName>
  );
};

export default PoliciesPage;
